import { z } from "zod";
export const APP_STARTER_CONTEXT_SCHEMA_VERSION = 2;
export const APP_STARTER_MAX_FILES = 500;
export const APP_STARTER_MAX_SOURCE_BYTES = 5 * 1024 * 1024;
export const APP_STARTER_MAX_CATALOG_SOURCE_BYTES = 20 * 1024 * 1024;
export const appStarterIdSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const appStarterFilePathSchema = z
    .string()
    .min(1)
    .max(240)
    .regex(/^(?:[A-Za-z0-9._@+-]+\/)*[A-Za-z0-9._@+-]+$/)
    .refine((value) => !value.startsWith("/") &&
    !value.includes("\\") &&
    !value.includes("\0") &&
    value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== ".."), "Starter file paths must be normalized relative POSIX paths");
export const appStarterFileSchema = z
    .object({
    path: appStarterFilePathSchema,
    contentBase64: z.string().max(Math.ceil(APP_STARTER_MAX_SOURCE_BYTES / 3) * 4 + 4),
    sizeBytes: z.number().int().min(0).max(APP_STARTER_MAX_SOURCE_BYTES),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
export const appStarterDefinitionSchema = z
    .object({
    id: appStarterIdSchema,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(1_000),
    technologies: z.array(z.string().trim().min(1).max(100)).min(1).max(20),
    useFor: z.array(z.string().trim().min(1).max(300)).min(1).max(20),
    revision: z.string().trim().min(1).max(80),
    sourceDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    guidePaths: z
        .array(z
        .string()
        .min(1)
        .max(244)
        .regex(/^app(?:\/[A-Za-z0-9._@+-]+)+$/))
        .min(1)
        .max(10),
    files: z.array(appStarterFileSchema).min(1).max(APP_STARTER_MAX_FILES),
})
    .strict();
export const appStarterPromptContextSchema = z
    .object({
    schemaVersion: z.literal(APP_STARTER_CONTEXT_SCHEMA_VERSION),
    generatedAt: z.iso.datetime(),
    policyMarkdown: z.string().min(1).max(32_768),
    policySha256: z.string().regex(/^[a-f0-9]{64}$/),
    catalogSha256: z.string().regex(/^[a-f0-9]{64}$/),
    prompt: z.string().min(1).max(64_000),
    promptSha256: z.string().regex(/^[a-f0-9]{64}$/),
    starters: z.array(appStarterDefinitionSchema).min(1).max(50),
})
    .strict();
export const APP_STARTER_MONITORING_GUIDANCE = `Before the first promotion, make one monitoring decision. Use OpenCloud's built-in app, deployment, operation, cron, background-job, HTTP-error-rate, and telemetry-freshness signals where they already describe the failure. If an important product failure is not covered, add a bounded custom metric and matching manifest alert rule, and record it through the supported browser SDK at the real failure boundary. Do not add custom monitoring or a fake failure when the product has no such need.`;
export const DEFAULT_APP_STARTER_POLICY = `Choose the starter after translating the user's message into concrete product requirements.

Use React + Vite + React Router for data-backed apps, multi-screen workflows, richer interactive forms, or products that call external services through OpenCloud Functions.

Use OpenCloud minimal for a focused single-screen app when the extra framework structure would not help the requested workflow.

Start every new app from the closest starter and adapt it. Do not rebuild foundation code that the selected starter already provides.

Use the selected starter's included semantic theme and accessible UI patterns. Reuse its components before creating equivalents, express company and product branding through semantic token overrides, and preserve accessible names, focus behavior, reduced motion, responsive layouts, and 44-pixel control targets.

Read every guidance path returned by starter materialization. For a component-based starter, treat its version-matched local UI guide as the source of truth for control composition and component APIs.

Do not copy OpenCloud logos or platform navigation into customer apps unless the user's requirements explicitly call for them.`;
export function renderAppStarterSelectionPrompt(policyMarkdown, starters) {
    const catalog = starters
        .map((starter) => `## ${starter.name} (\`${starter.id}\`)

${starter.description}

- Technologies: ${starter.technologies.join(", ")}
- Good fit: ${starter.useFor.join("; ")}
- Revision: ${starter.revision}
- Guides after installation: ${starter.guidePaths
        .map((guidePath) => `\`${guidePath}\``)
        .join(", ")}`)
        .join("\n\n");
    return `# Platform app starters and guidance

For a new app with an empty source workspace, first translate the user's message into a bounded set of product requirements. Then choose one starter using the platform guidance and catalog below. The source-writing agent must call \`materialize_app_starter\` exactly once before ordinary source edits, then read every guidance path returned by that tool and build by changing the materialized files.

For an existing app, preserve its current source and do not select or materialize a starter.

Every materialized starter contains its own \`STARTER.md\` with exact local build and editing guidance. Component-based starters may also return a version-matched local design-system guide. Use every returned guide and the included components or patterns as the foundation for the requested product. Product requirements and the platform-admin guidance may change the theme and composition, but should not cause the agent to recreate working foundation code.

## Platform starter and app guidance

${policyMarkdown.trim()}

## Monitoring decision

${APP_STARTER_MONITORING_GUIDANCE}

## Available starters

${catalog}`;
}
//# sourceMappingURL=starters.js.map